// Pure spec-decoder factory — accepts a schema object and returns an
// environment-agnostic resolver that maps `(message, path)` to schema
// metadata (description, type name, in-spec / extension flag). Used by the
// UI `JsonTree` for hover-tooltips and the `⚠ ext` badge.
//
// Same two-entry-point pattern as `validate-factory.ts`: Node uses
// `spec-decoder.ts` (createRequire), browser uses `spec-decoder-browser.ts`
// (Vite JSON import).
/**
 * Build a spec decoder bound to the given ACP schema. The factory builds the
 * `method → def` index up-front; per-field lookups are linear in the path
 * length and effectively O(1) amortised (≤5 segments in practice).
 */
export function createSpecDecoder(schema) {
    const defs = schema.$defs ?? {};
    const methodIndex = new Map();
    for (const [name, def] of Object.entries(defs)) {
        const method = def['x-method'];
        if (typeof method !== 'string')
            continue;
        let slots = methodIndex.get(method);
        if (!slots) {
            slots = {};
            methodIndex.set(method, slots);
        }
        if (name.endsWith('Request'))
            slots.requestDef = name;
        else if (name.endsWith('Response'))
            slots.responseDef = name;
        else if (name.endsWith('Notification'))
            slots.notificationDef = name;
    }
    function pickDef(message, pairedMethod) {
        switch (message.kind) {
            case 'request': {
                const m = message.method;
                if (!m)
                    return null;
                return methodIndex.get(m)?.requestDef ?? null;
            }
            case 'notification': {
                const m = message.method;
                if (!m)
                    return null;
                return methodIndex.get(m)?.notificationDef ?? null;
            }
            case 'response':
            case 'error': {
                // Responses don't carry their own method — we need the
                // paired request's method to pick the right def.
                if (!pairedMethod)
                    return null;
                return methodIndex.get(pairedMethod)?.responseDef ?? null;
            }
            default:
                return null;
        }
    }
    function envelopeKey(kind) {
        if (kind === 'request' || kind === 'notification')
            return 'params';
        if (kind === 'response')
            return 'result';
        if (kind === 'error')
            return 'error';
        return null;
    }
    function isPlainObject(v) {
        return typeof v === 'object' && v !== null && !Array.isArray(v);
    }
    /**
     * Check if a value matches every `const`/`enum` discriminator declared on
     * a variant's properties. Handles JSON Schema "tagged union" patterns
     * like `{type: 'boolean', value: ...}` vs `{type: 'string', value: ...}`.
     */
    function discriminatorsMatch(variant, value) {
        const props = variant.properties;
        if (!props)
            return true;
        for (const [key, prop] of Object.entries(props)) {
            if (!prop || typeof prop !== 'object')
                continue;
            if ('const' in prop) {
                if (value[key] !== prop.const)
                    return false;
            }
            else if (Array.isArray(prop.enum)) {
                if (!prop.enum.includes(value[key]))
                    return false;
            }
        }
        return true;
    }
    function variantMatchesValue(variant, value) {
        const type = variant.type;
        if (typeof type === 'string') {
            if (type === 'null')
                return value === null;
            if (type === 'string')
                return typeof value === 'string';
            if (type === 'boolean')
                return typeof value === 'boolean';
            if (type === 'number' || type === 'integer')
                return typeof value === 'number';
            if (type === 'array')
                return Array.isArray(value);
            if (type === 'object') {
                return isPlainObject(value) && discriminatorsMatch(variant, value);
            }
        }
        if (variant.$ref || variant.properties || variant.allOf) {
            return isPlainObject(value) && discriminatorsMatch(variant, value);
        }
        return false;
    }
    function resolveRef(node, value = undefined) {
        if (!node)
            return null;
        if (node.$ref) {
            const refName = node.$ref.replace(/^#\/\$defs\//, '');
            const target = defs[refName];
            return target ? resolveRef(target, value) : null;
        }
        // `allOf` with a single $ref is the SDK's way of attaching descriptions
        // — collapse it for property lookup.
        if (node.allOf && node.allOf.length > 0) {
            const merged = { properties: {} };
            for (const part of node.allOf) {
                const resolved = resolveRef(part, value);
                if (resolved?.properties) {
                    merged.properties = { ...merged.properties, ...resolved.properties };
                }
                if (resolved?.type && !merged.type)
                    merged.type = resolved.type;
                if (resolved?.items && !merged.items)
                    merged.items = resolved.items;
            }
            // Carry over node-level description if `allOf` wrapped it.
            if (node.description && !merged.description)
                merged.description = node.description;
            return merged;
        }
        if (node.anyOf || node.oneOf) {
            const variants = (node.anyOf ?? node.oneOf);
            // When we have the actual value, disambiguate by JSON type — this
            // is what makes the `ⓘ` popover say STRING for a string and BOOLEAN
            // for a boolean instead of always picking the first variant.
            if (value !== undefined) {
                for (const v of variants) {
                    if (v.type === 'null')
                        continue;
                    if (variantMatchesValue(v, value))
                        return resolveRef(v, value);
                }
            }
            // Fallback when no value (intermediate schema lookup) or no match:
            // first non-null wins.
            for (const v of variants) {
                if (v.type === 'null')
                    continue;
                const resolved = resolveRef(v, value);
                if (resolved)
                    return resolved;
            }
            return null;
        }
        return node;
    }
    function valueAtPath(root, path) {
        let v = root;
        for (const seg of path) {
            if (v === null || v === undefined)
                return undefined;
            if (Array.isArray(v) && /^\d+$/.test(seg)) {
                v = v[Number(seg)];
            }
            else if (typeof v === 'object') {
                v = v[seg];
            }
            else {
                return undefined;
            }
        }
        return v;
    }
    function typeNameOf(node) {
        if (!node)
            return undefined;
        if (node.$ref)
            return node.$ref.replace(/^#\/\$defs\//, '');
        const t = node.type;
        if (typeof t === 'string')
            return t;
        if (Array.isArray(t)) {
            const nonNull = t.find((x) => x !== 'null');
            return nonNull;
        }
        return undefined;
    }
    function buildResolver(defName, rootValue) {
        const def = defs[defName];
        return {
            typeName: defName,
            typeDescription: def?.description ?? null,
            resolve(path) {
                if (path.length === 0) {
                    return {
                        inSpec: true,
                        isExtension: false,
                        description: def?.description,
                        type: defName,
                    };
                }
                // Path includes envelope key — skip it. The envelope key
                // (params/result/error) is also where rootValue starts
                // descending from, so the value at path[0] is the payload's
                // params/result/error sub-object.
                const inner = path.slice(1);
                if (inner.length === 0)
                    return null;
                let isExtension = false;
                let current = def ?? null;
                for (let i = 0; i < inner.length; i++) {
                    const segment = inner[i];
                    if (segment === '_meta')
                        isExtension = true;
                    const isIndex = /^\d+$/.test(segment);
                    // Disambiguate `current`'s shape using the value AT THIS
                    // depth — for example, when `current` is an array of
                    // (boolean | string) variants and the actual value is a
                    // string, we want the string variant.
                    const valueHere = valueAtPath(rootValue, path.slice(0, i + 1));
                    current = resolveRef(current, valueHere);
                    if (!current)
                        return null;
                    if (isIndex) {
                        const itemValue = valueAtPath(rootValue, path.slice(0, i + 2));
                        current = resolveRef(current.items, itemValue);
                        continue;
                    }
                    if (isExtension && segment !== '_meta') {
                        return { inSpec: false, isExtension: true };
                    }
                    const next = current.properties?.[segment];
                    if (!next)
                        return { inSpec: false, isExtension };
                    current = next;
                }
                // Terminal resolve — pass the actual value so unions surface
                // the correct variant's description/type.
                const finalValue = valueAtPath(rootValue, path);
                const resolved = resolveRef(current, finalValue);
                const info = { inSpec: true, isExtension };
                if (current?.description)
                    info.description = current.description;
                else if (resolved?.description)
                    info.description = resolved.description;
                const type = typeNameOf(current ?? null) ?? typeNameOf(resolved);
                if (type)
                    info.type = type;
                if (resolved?.enum)
                    info.enumValues = resolved.enum;
                return info;
            },
        };
    }
    function resolveForMessage(message, opts = {}) {
        const defName = pickDef(message, opts.pairedMethod);
        if (!defName)
            return null;
        const _envelope = envelopeKey(message.kind);
        if (!_envelope)
            return null;
        return buildResolver(defName, message.payload);
    }
    function knownMethods() {
        return [...methodIndex.keys()].sort();
    }
    return { resolveForMessage, knownMethods };
}
//# sourceMappingURL=spec-decoder-factory.js.map